import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Radar, CheckCircle2, LogIn, UserPlus } from "lucide-react";

export default function ClaimWant() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!token) { setErr("This claim link is missing its token."); return; }
    api.get(`/match/demands/claim/${encodeURIComponent(token)}`).then(({ data }) => setInfo(data))
      .catch((ex) => setErr(formatApiError(ex.response?.data?.detail) || "This claim link is invalid."));
  }, [token]);

  useEffect(() => {
    if (!user || !info || info.claimed || claiming) return;
    setClaiming(true);
    api.post("/match/demands/claim", { token }).then(() => {
      localStorage.removeItem("vsk_new_buyer");
      toast.success("Want claimed — matches will show on your Wanted page");
      nav("/wanted", { replace: true });
    }).catch((ex) => { setErr(formatApiError(ex.response?.data?.detail) || "Could not claim this want."); setClaiming(false); });
  }, [user, info]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = encodeURIComponent(`/claim?token=${token}`);

  return (
    <div className="max-w-xl mx-auto px-6 py-16" data-testid="claim-want-page">
      <div className="text-xs uppercase tracking-widest text-[#FF3B30] flex items-center gap-2"><Radar className="h-3.5 w-3.5" /> Claim your want</div>
      {err && <div className="mt-4 rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-4 text-sm text-[#FF3B30]" data-testid="claim-error">{err}</div>}
      {info && (
        <div className="mt-4 rounded-xl border border-[#27272A] bg-[#121212] p-6">
          <div className="text-lg font-black tracking-tight" data-testid="claim-want-text">&ldquo;{info.text}&rdquo;</div>
          <div className="mt-1 text-sm text-neutral-400">
            {info.matches_total} match{info.matches_total === 1 ? "" : "es"} so far &middot; alerts go to <span className="font-mono text-neutral-300">{info.email_masked}</span>
          </div>
          {info.claimed && !user && <div className="mt-3 text-sm text-[#34C759] flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Already claimed — log in to see it.</div>}
          {loading || (user && !err) ? (
            <div className="mt-5 text-sm text-neutral-400" data-testid="claim-progress">{user ? "Attaching this want to your account…" : "Checking your session…"}</div>
          ) : (
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to={`/register?role=buyer&email=${encodeURIComponent(info.email)}&next=${next}`}>
                <Button data-testid="claim-register-btn" className="rounded-full bg-[#FF3B30] hover:bg-[#e0352b] text-white font-semibold px-5"><UserPlus className="h-4 w-4 mr-2" /> Create free account &amp; claim</Button>
              </Link>
              <Link to={`/login?next=${next}`}>
                <Button variant="outline" data-testid="claim-login-btn" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 hover:text-white px-5"><LogIn className="h-4 w-4 mr-2" /> I have an account</Button>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
